import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import CommonUtil from 'utils/CommonUtil';
import { DEFAULT_TIMEZONE } from 'vd-constants';

const LOCAL_TIME_FIELDS = new Set(['startTimeLocal', 'endTimeLocal']);

export const serializeDates = (value: unknown, timezone: string, key?: string): unknown => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return key && LOCAL_TIME_FIELDS.has(key)
      ? moment.tz(value, timezone).toISOString(true)
      : value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => serializeDates(item, timezone));
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entry]) => [
        entryKey,
        serializeDates(entry, timezone, entryKey),
      ]),
    );
  }
  return value;
};

export interface JSONModalProps {
  src: object;
  isModalOpen: boolean;
  closeModal: () => void;
  title?: string;
  timezone?: string;
}

export interface JSONCopyNotifier {
  success: (message: string) => void;
  error: (message: string) => void;
}

export function useJSONModalActions(
  { src, closeModal, title = '', timezone }: JSONModalProps,
  notify: JSONCopyNotifier,
) {
  const locale = useLocale();
  const serializedSrc = useMemo(
    () => serializeDates(src, timezone || DEFAULT_TIMEZONE) as object,
    [src, timezone],
  );
  const copyJSONToClipboard = useCallback(() => {
    const data = JSON.stringify(serializedSrc, null, 2);
    CommonUtil.copyStringToClipboard({
      string: data,
      successCallback: () => {
        notify.success('Copied JSON to clipboard');
      },
      errorCallback: () => {
        notify.error('Failed to copy JSON to clipboard');
      },
    });
  }, [notify, serializedSrc]);

  return {
    locale,
    serializedSrc,
    copyJSONToClipboard,
    title: `${title} JSON Data`,
    closeModal,
  };
}
